import type { Modus } from '../shared/pruefpunkte.js';
import { lies } from './speicher.js';

export class ApiFehler extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly daten?: unknown,
  ) {
    super(message);
  }
}

export class NetzFehler extends Error {
  constructor() {
    super('Keine Verbindung zum Server. Netz prüfen und erneut versuchen.');
  }
}

export interface ApiOptionen {
  /** Für Auswertung und Druckseite: zuerst den Zugang nur für die Auswertung nehmen. */
  auswertung?: boolean;
  methode?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  json?: unknown;
  body?: BodyInit;
  kopf?: Record<string, string>;
  /** Token eines bestimmten Modus; null = ohne Anmeldung */
  modus?: Modus | null;
  zeitlimitMs?: number;
}

let aktuellerModus: Modus = 'browser';

export function setzeModus(modus: Modus): void {
  aktuellerModus = modus;
}

export function modus(): Modus {
  return aktuellerModus;
}

export async function token(m: Modus = aktuellerModus): Promise<string | undefined> {
  return lies(`token:${m}`);
}

/**
 * Token für Anfragen: das des Geräts, sonst der Zugang nur für Auswertung und
 * Druckseite. Auf diesen beiden Seiten zuerst der Zugang, damit eine Ablehnung
 * eindeutig einem Token zugeordnet werden kann.
 */
async function anmeldeToken(m: Modus, auswertung = false): Promise<string | undefined> {
  const geraet = await token(m);
  const zugang = await lies('token:auswertung');
  return auswertung ? (zugang ?? geraet) : (geraet ?? zugang);
}

/** Kopf für eigene fetch-Aufrufe auf Auswertung und Druckseite (SVG, CSV). */
export async function authKopf(): Promise<Record<string, string>> {
  const t = await anmeldeToken(aktuellerModus, true);
  return t ? { authorization: `Bearer ${t}` } : {};
}

export async function api<T>(pfad: string, optionen: ApiOptionen = {}): Promise<T> {
  const kopf: Record<string, string> = { ...optionen.kopf };
  if (optionen.modus !== null) {
    const t = await anmeldeToken(optionen.modus ?? aktuellerModus, optionen.auswertung);
    if (t) kopf.authorization = `Bearer ${t}`;
  }
  let body = optionen.body;
  if (optionen.json !== undefined) {
    kopf['content-type'] = 'application/json';
    body = JSON.stringify(optionen.json);
  }

  let antwort: Response;
  try {
    antwort = await fetch(pfad, {
      method: optionen.methode ?? (body ? 'POST' : 'GET'),
      headers: kopf,
      ...(body !== undefined ? { body } : {}),
      cache: 'no-store',
      signal: AbortSignal.timeout(optionen.zeitlimitMs ?? 20_000),
    });
  } catch {
    throw new NetzFehler();
  }

  if (antwort.status === 204) return undefined as T;
  const text = await antwort.text();
  let daten: unknown;
  try {
    daten = text ? JSON.parse(text) : undefined;
  } catch {
    daten = text;
  }
  if (!antwort.ok) {
    const meldung = (daten as { fehler?: string } | undefined)?.fehler ?? `Fehler ${antwort.status}`;
    throw new ApiFehler(antwort.status, meldung, daten);
  }
  return daten as T;
}

/** Ist der Server erreichbar? Kurz, damit ein Start ohne Netz nicht hängt. */
export async function serverErreichbar(): Promise<boolean> {
  try {
    await api('/api/status', { modus: null, zeitlimitMs: 4_000 });
    return true;
  } catch {
    return false;
  }
}

export function fehlerText(fehler: unknown): string {
  if (fehler instanceof ApiFehler || fehler instanceof NetzFehler) return fehler.message;
  if (fehler instanceof DOMException) return `${fehler.name}: ${fehler.message}`;
  if (fehler instanceof Error) return fehler.message;
  return String(fehler);
}
